import { GoogleMap, Libraries, useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CctvResponse, deviceApi, GasDetectorResponse, HookResponse } from 'modules/maps';
import { MarkerVisibility } from '../../../../Dashboard/Ptw/view';
import { CctvMarker } from './components/Cctv/cctv-marker.ts';
import { GasDetectorMarker } from './components/iot/iot-marker.ts';
import { HookMarker } from './components/hook/hook-marker.ts';
import { GeometryMarker } from './components/geometry/geometry-marker.ts';
import { DeviceConnectionLines } from './components/geometry/device-connection-lines.ts';
import { SelectionOverlay } from './components/geometry/selection-overlay.ts';
import { LocationLines } from './components/geometry/location-lines.ts';
import { createTintedPinIcon } from './components/pin-icon.ts';
import { useMapOverlays } from './useMapOverlays.ts';
import { useSetRecoilState } from 'recoil';
import { LoadingAtom } from '../../../../../modules';
import { ptwMapImages } from '../../../../../constants';
import { CctvWrapper } from './components/Cctv';

const CENTER = {
  lat: 24.1290482,
  lng: 52.7963177
};

const GOOGLE_MAP_LIBRARIES = ['marker'] as unknown as Libraries;

// prop을 안 넘겼을 때 기본값으로 매 렌더마다 새 배열이 생성되는 것을 막기 위한 안정적인 빈 배열
const EMPTY_ARRAY: never[] = [];

const WORK_TYPE_ICONS: Record<'CW' | 'HW' | 'SW', string> = {
  CW: createTintedPinIcon('#1976D2'), // Cold Work - blue
  HW: createTintedPinIcon('#FB8C00'), // Hot Work - orange
  SW: createTintedPinIcon('#43A047') // Confined Space Work - green
};

const WORK_TYPES: Array<'CW' | 'HW' | 'SW'> = ['CW', 'HW', 'SW'];

// location JSON에는 permit_type 필드가 없어서(백엔드 DashboardService가 주입하지 않음),
// 확실히 존재하는 ptw_no(label)의 앞 두 글자로 permit 타입을 판별한다.
const getPermitType = (item: any): string =>
  String(item?.ptw_no ?? item?.label ?? '')
    .slice(0, 2)
    .toUpperCase();

const isKnownWorkType = (item: any) => WORK_TYPES.includes(getPermitType(item) as any);

type MapProps = {
  cctvList?: CctvResponse[];
  gasDetectorList?: GasDetectorResponse[];
  hookList?: HookResponse[];
  locationData?: any[];
  selectedCctvList?: CctvResponse[];
  onSelectCctv?: (cctv: CctvResponse | null) => void;
  onCloseCctv?: (cctv: CctvResponse) => void;
  onSelectHook?: (hook: HookResponse | null) => void;
  onSelectGas?: (hook: GasDetectorResponse | null) => void;
  markerVisibility?: MarkerVisibility;
  onSelectLocation?: (
    item: any,
    clickedPoint?: { lat: number; lng: number },
    pos?: { clientX: number; clientY: number }
  ) => void;
  onRightClickLocation?: (item: any, pos: { clientX: number; clientY: number }) => void;
  onMapMove?: () => void;
  selectedLocation?: any;
};

export const Map = ({
  cctvList = EMPTY_ARRAY,
  gasDetectorList = EMPTY_ARRAY,
  hookList = EMPTY_ARRAY,
  locationData,
  selectedCctvList = EMPTY_ARRAY,
  onSelectCctv,
  onCloseCctv,
  onSelectHook,
  onSelectGas,
  onMapMove,
  markerVisibility,
  onSelectLocation,
  onRightClickLocation,
  selectedLocation
}: MapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [clickedPoint, setClickedPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [relatedDevices, setRelatedDevices] = useState<any>(null);
  const [focusOrder, setFocusOrder] = useState<number[]>([]);
  const prevSeqsRef = useRef<number[]>([]);

  useEffect(() => {
    const seqs = selectedCctvList.map((c) => c.seq);
    const prev = prevSeqsRef.current;
    prevSeqsRef.current = seqs;
    setFocusOrder((order) => {
      const kept = order.filter((s) => seqs.includes(s));
      const added = seqs.filter((s) => !prev.includes(s));
      return [...kept, ...added];
    });
  }, [selectedCctvList]);

  const bringToFront = (seq: any) => {
    setFocusOrder((prev) => [...prev.filter((s) => s !== seq), seq]);
  };

  const setLoading = useSetRecoilState(LoadingAtom);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-saf',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_GS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES
  });

  const { floorOn, setFloorOn, mapRef } = useMapOverlays(map, isLoaded);

  const generalWorkData = useMemo(
    () => locationData?.filter((item: any) => item.work_risk === 'GENERAL'),
    [locationData]
  );
  const highRiskWorkData = useMemo(
    () => locationData?.filter((item: any) => item.work_risk === 'HIGH'),
    [locationData]
  );
  const generalLinesData = useMemo(
    () => locationData?.filter((item: any) => item.lines?.length && item.work_risk === 'GENERAL'),
    [locationData]
  );
  const highRiskLinesData = useMemo(
    () => locationData?.filter((item: any) => item.lines?.length && item.work_risk === 'HIGH'),
    [locationData]
  );
  const generalWorkByType = useMemo(
    () =>
      WORK_TYPES.map((type) => ({
        type,
        points: generalWorkData?.filter((item: any) => getPermitType(item) === type) ?? []
      })),
    [generalWorkData]
  );
  const highRiskWorkByType = useMemo(
    () =>
      WORK_TYPES.map((type) => ({
        type,
        points: highRiskWorkData?.filter((item: any) => getPermitType(item) === type) ?? []
      })),
    [highRiskWorkData]
  );
  // CW/HW/SW가 아닌 경우(예상 밖 값) 마커가 누락되지 않도록 기존 아이콘으로 표시
  const generalWorkFallbackData = useMemo(
    () => generalWorkData?.filter((item: any) => !isKnownWorkType(item)),
    [generalWorkData]
  );
  const highRiskWorkFallbackData = useMemo(
    () => highRiskWorkData?.filter((item: any) => !isKnownWorkType(item)),
    [highRiskWorkData]
  );
  const roadClosureData = useMemo(
    () => locationData?.filter((item: any) => getPermitType(item) === 'RC'),
    [locationData]
  );
  const roadClosureLinesData = useMemo(
    () => locationData?.filter((item: any) => item.lines?.length && getPermitType(item) === 'RC'),
    [locationData]
  );
  useEffect(() => {
    //console.log(333, locationData)
  }, [locationData]);

  useEffect(() => {
    setRelatedDevices(null);
    if (selectedLocation) {
      setLoading(true);
      deviceApi
        .findIotDeviceList(selectedLocation.ptw_no)
        .then((response) => {
          setRelatedDevices(response.resultObject);
        })
        .finally(function () {
          setLoading(false);
        });
    }
  }, [selectedLocation]);

  if (!isLoaded) return <div />;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1 }}>
        <button
          onClick={() => setFloorOn(!floorOn)}
          style={{
            padding: '10px 18px',
            backgroundColor: floorOn ? '#055cb4' : '#ffffff',
            color: floorOn ? '#ffffff' : '#333333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'all 0.2s ease'
          }}
        >
          {floorOn ? 'Hide Plot Plan' : 'Show Plot Plan'}
        </button>
      </div>

      <GoogleMap
        onLoad={(m) => {
          mapRef.current = m;
          setMap(m);
        }}
        onUnmount={() => setMap(null)}
        onZoomChanged={onMapMove}
        center={CENTER}
        zoom={17}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        options={{
          mapId: import.meta.env.VITE_GOOGLE_MAP_ID_GS_API_KEY,
          mapTypeControl: false,
          fullscreenControl: false,
          mapTypeId: google.maps.MapTypeId.HYBRID
        }}
      />

      {markerVisibility?.cctv !== false && (
        <CctvMarker
          map={map}
          isLoaded={isLoaded}
          cctvList={cctvList}
          onSelectCctv={(cctv) => {
            bringToFront(cctv?.seq);
            onSelectCctv?.(cctv);
          }}
        />
      )}
      {markerVisibility?.gasDetector !== false && (
        <GasDetectorMarker
          map={map}
          isLoaded={isLoaded}
          gasDetectorList={gasDetectorList}
          onSelectGas={onSelectGas}
        />
      )}
      {markerVisibility?.smartHook !== false && (
        <HookMarker map={map} isLoaded={isLoaded} hookList={hookList} onSelectHook={onSelectHook} />
      )}
      {markerVisibility?.generalWork !== false && (
        <>
          {generalWorkByType.map(({ type, points }) => (
            <GeometryMarker
              key={`general-${type}`}
              map={map}
              isLoaded={isLoaded}
              locationData={points}
              markerIcon={WORK_TYPE_ICONS[type]}
              onSelectLocation={(item, point, pos) => {
                setClickedPoint(point ?? null);
                onSelectLocation?.(item, point, pos);
              }}
              onRightClick={onRightClickLocation}
            />
          ))}
          <GeometryMarker
            map={map}
            isLoaded={isLoaded}
            locationData={generalWorkFallbackData}
            markerIcon={ptwMapImages.generalWork}
            onSelectLocation={(item, point, pos) => {
              setClickedPoint(point ?? null);
              onSelectLocation?.(item, point, pos);
            }}
            onRightClick={onRightClickLocation}
          />
        </>
      )}
      {markerVisibility?.highRiskWork !== false && (
        <>
          {highRiskWorkByType.map(({ type, points }) => (
            <GeometryMarker
              key={`high-${type}`}
              map={map}
              isLoaded={isLoaded}
              locationData={points}
              markerIcon={ptwMapImages.highRiskWork}
              onSelectLocation={(item, point, pos) => {
                setClickedPoint(point ?? null);
                onSelectLocation?.(item, point, pos);
              }}
              onRightClick={onRightClickLocation}
            />
          ))}
          <GeometryMarker
            map={map}
            isLoaded={isLoaded}
            locationData={highRiskWorkFallbackData}
            markerIcon={ptwMapImages.highRiskWork}
            onSelectLocation={(item, point, pos) => {
              setClickedPoint(point ?? null);
              onSelectLocation?.(item, point, pos);
            }}
            onRightClick={onRightClickLocation}
          />
        </>
      )}

      {markerVisibility?.roadClosure !== false && (
        <>
          <LocationLines
            map={map}
            isLoaded={isLoaded}
            locationData={generalLinesData}
            onRightClick={onRightClickLocation}
          />
          <LocationLines
            map={map}
            isLoaded={isLoaded}
            locationData={highRiskLinesData}
            onRightClick={onRightClickLocation}
          />
          <GeometryMarker
            map={map}
            isLoaded={isLoaded}
            locationData={roadClosureData}
            markerIcon={ptwMapImages.roadClosure}
            onSelectLocation={(item, point, pos) => {
              setClickedPoint(point ?? null);
              onSelectLocation?.(item, point, pos);
            }}
            onRightClick={onRightClickLocation}
          />
          <LocationLines
            map={map}
            isLoaded={isLoaded}
            locationData={roadClosureLinesData}
            onRightClick={onRightClickLocation}
          />
        </>
      )}

      <SelectionOverlay
        map={map}
        isLoaded={isLoaded}
        clickedPoint={selectedLocation ? clickedPoint : null}
      />

      {selectedLocation && clickedPoint && relatedDevices && (
        <DeviceConnectionLines
          map={map}
          isLoaded={isLoaded}
          clickedPoint={clickedPoint}
          relatedDevices={relatedDevices}
        />
      )}

      {selectedCctvList.map((cctv, i) => (
        <CctvWrapper
          key={cctv.seq}
          cctv={cctv}
          index={i}
          zIndex={20 + focusOrder.indexOf(cctv.seq)}
          isFocused={focusOrder[focusOrder.length - 1] === cctv.seq}
          onFocus={() => bringToFront(cctv.seq)}
          onClose={() => onCloseCctv?.(cctv)}
        />
      ))}
    </div>
  );
};
